import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'

import FilesManager from '../shared/FilesManager'
import AreaManager from '../shared/AreaManager'
import SmartTable, { SmartTableDataTypes } from '../shared/SmartTable'
import { validateFormInput } from '../../utils'
import Constraints from './Constraints/Constraints'
import { Card, List } from 'antd'
import InstitutionOptionTable, { BOOLEAN_FIELD, STRING_FIELD } from './InstitutionOptionTable'
import { GlobalContext } from '../../Providers/GlobalProvider'
import { faFile, faGlobe } from '@fortawesome/pro-solid-svg-icons'
import { InstitutionContext } from '../../Providers/InstitutionProvider'
import TitleIcon from '../icons/TitleIcon'
import { onError, onSuccess } from '../../utils/apiHelper'
import { useSelector } from 'react-redux'
import { getInstitutionDocuments, updateInstitution } from '../../utils/api/institution'
import { selectTranslate } from '../../reducers/LocaleReducer/LocalReducer'
import { selectUser } from '../../reducers/UserReducer'

const CARD_IDENTITY_TITLE = 'Identity of the institution'
const CARD_OPTION_TITLE = 'Institution Options'

const listId = [
  'institution-identity-list',
  'institution-student-data-list',
  'institution-options-list'
]

const InstitutionParameters = () => {
  const { institution, loading, reload } = useContext(InstitutionContext)
  const { countries, languages } = useContext(GlobalContext)

  const t = useSelector(selectTranslate)
  const user = useSelector(selectUser)

  const [selectedInstitutionFiles, setSelectedInstitutionFiles] = useState(null)
  const [selectedInstitution, setSelectedInstitution] = useState(null)
  const [selectedInstitutionArea, setSelectedInstitutionArea] = useState(null)
  const [institutionIdentityFocus, setInstitutionIdentityFocus] = useState(true)
  const [institutionStudentDataFocus, setInstitutionStudentDataFocus] = useState(true)
  const [institutionOptionsFocus, setInstitutionOptionsFocus] = useState(true)
  const [localCountries, setLocalCountries] = useState([])

  const identityRef = useRef(null)
  const studentDataRef = useRef(null)
  const optionsRef = useRef(null)

  const observer = useMemo(
    () =>
      new IntersectionObserver(([entry]) => { // eslint-disable-line no-undef
        switch (entry.target) {
          case identityRef.current:
            setInstitutionIdentityFocus(entry.isIntersecting)
            break
          case studentDataRef.current:
            setInstitutionStudentDataFocus(entry.isIntersecting)
            break
          case optionsRef.current:
            setInstitutionOptionsFocus(entry.isIntersecting)
            break
          default:
        }
      }),
    []
  )

  useEffect(() => {
    observer.observe(identityRef.current)
    observer.observe(studentDataRef.current)
    observer.observe(optionsRef.current)

    return () => {
      observer.disconnect()
    }
  }, [identityRef, studentDataRef, optionsRef, observer])

  useEffect(() => {
    if (institutionIdentityFocus) {
      document.getElementById('institution-identity-list').style.backgroundColor = 'lightgray'
      document.getElementById('institution-student-data-list').style.backgroundColor = 'white'
      document.getElementById('institution-options-list').style.backgroundColor = 'white'
    }

    if (institutionStudentDataFocus && !institutionIdentityFocus) {
      document.getElementById('institution-identity-list').style.backgroundColor = 'white'
      document.getElementById('institution-student-data-list').style.backgroundColor = 'lightgray'
      document.getElementById('institution-options-list').style.backgroundColor = 'white'
    }

    if (institutionOptionsFocus && !institutionStudentDataFocus && !institutionIdentityFocus) {
      document.getElementById('institution-identity-list').style.backgroundColor = 'white'
      document.getElementById('institution-student-data-list').style.backgroundColor = 'white'
      document.getElementById('institution-options-list').style.backgroundColor = 'lightgray'
    }
  }, [institutionIdentityFocus, institutionStudentDataFocus, institutionOptionsFocus])

  useEffect(() => {
    setLocalCountries(countries.map(c => {
      return { id: c.id, name: t('country.' + c.code) }
    }))
  }, [countries, t])

  const handleEditInstitution = institution => {
    const updatedInstitution = { ...institution }

    delete updatedInstitution.filesCount

    updateInstitution(user, institution).then(() => {
      onSuccess(t('institution.update.success'))
      reload()
    }).catch(() => {
      onError(t('institution.update.error'))
    })
  }

  const handleClick = key => {
    switch (key) {
      case 0:
        if (identityRef && identityRef.current) {
          identityRef.current.scrollIntoView()
          setInstitutionIdentityFocus(true)
          setInstitutionStudentDataFocus(true)
          setInstitutionOptionsFocus(true)
        }
        break
      case 1:
        if (studentDataRef && studentDataRef.current) {
          studentDataRef.current.scrollIntoView()
          setInstitutionIdentityFocus(false)
          setInstitutionStudentDataFocus(true)
          setInstitutionOptionsFocus(true)
        }
        break
      case 2:
        if (optionsRef && optionsRef.current) {
          optionsRef.current.scrollIntoView()
          setInstitutionIdentityFocus(false)
          setInstitutionStudentDataFocus(false)
          setInstitutionOptionsFocus(true)
        }
        break
      default:
    }
  }

  const fetchDocuments = () => {
    getInstitutionDocuments(user, institution).then(json => {
      if (json?.data) {
        setSelectedInstitutionFiles(json.data)
        setSelectedInstitution(institution)
      } else {
        setSelectedInstitutionFiles(null)
      }
    })
  }

  const render = () => {
    let data = []

    if (institution) {
      data = [{
        id: institution.id,
        name: institution.name,
        email: institution.email,
        languageLocale: institution.languageLocale?.languageTag ?? null,
        address: institution.address,
        zipcode: institution.zipcode,
        country: institution.country ? institution.country.id : null,
        city: institution.city,
        description: institution.description,
        filesCount: institution.filesCount,
        globalMessage: institution.globalMessage,
        area: institution.areas ? institution.areas.map(element => element.localName).join(', ') : t('No region linked')
      }]
    }

    const columnsIdentity = [
      { type: SmartTableDataTypes.ID, key: 'id' },
      { type: SmartTableDataTypes.STRING, name: t('Name'), key: 'name', validate: (data) => validateFormInput('freeText', data, true) },
      {
        type: SmartTableDataTypes.STRING,
        name: t('Global message'),
        key: 'globalMessage',
        tooltipTitle: t('institution_parameters.global_message_column.tooltip')
      },
      { type: SmartTableDataTypes.STRING, name: t('Email'), key: 'email', validate: (data) => validateFormInput('freeText', data) },
      { type: SmartTableDataTypes.STRING, name: t('Address'), key: 'address', validate: (data) => validateFormInput('freeText', data, true) },
      { type: SmartTableDataTypes.SELECT, name: t('Language'), key: 'languageLocale', options: languages.flatMap(l => l.languageLocales).map(ll => ({ id: ll.languageTag, name: ll.nameInNativeLanguage })) },
      { type: SmartTableDataTypes.STRING, name: t('Zipcode'), key: 'zipcode', validate: (data) => validateFormInput('freeText', data, true) },
      { type: SmartTableDataTypes.STRING, name: t('City'), key: 'city', validate: (data) => validateFormInput('name', data, true) },
      { type: SmartTableDataTypes.SELECT, name: t('Country'), key: 'country', options: localCountries },
      {
        type: SmartTableDataTypes.STRING,
        name: <TitleIcon title={t('Areas')} tooltip={t('descriptionAreas')} />,
        key: 'area',
        validate: (data) => validateFormInput('freeText', data, false),
        disabled: true,
        disabledTooltip: true
      }
    ]

    const columnsOptions = [
      { type: STRING_FIELD, name: 'Description' },
      { name: 'Value' },
      { type: BOOLEAN_FIELD, name: 'Enabled' }
    ]

    return (
      <div className='institution-parameters'>
        <div className='parameters-navbar'>
          <List
            className='list-parameters'
            size='large'
            bordered
            dataSource={[
              t('Identity of the institution'),
              t('Student data required for an internship'),
              t('Institution Options')
            ]}
            renderItem={(item, key) => <List.Item id={listId[key]} onClick={() => { handleClick(key) }}>{item}</List.Item>}
          />
        </div>
        <div className='parameters'>
          <div className='institution-identity' ref={identityRef}>
            <Card title={t(CARD_IDENTITY_TITLE)}>
              <SmartTable
                hideTableControls
                columns={columnsIdentity}
                data={data}
                loading={loading}
                onDataEdit={handleEditInstitution}
                additionalActions={[
                  { iconName: faFile, type: 'primary', title: t('Manage files'), onClick: fetchDocuments },
                  { iconName: faGlobe, type: 'primary', title: t('linked-regions'), onClick: () => setSelectedInstitutionArea(institution) }
                ]}
              />
            </Card>
          </div>
          <div className='institution-student-data' ref={studentDataRef}>
            <Constraints />
          </div>
          <div className='institution-options' ref={optionsRef}>
            <Card title={t(CARD_OPTION_TITLE)}>
              <InstitutionOptionTable columns={columnsOptions} />
            </Card>
          </div>
          <FilesManager
            files={selectedInstitutionFiles}
            entityName='Institution'
            entity={selectedInstitution}
            onClose={() => {
              setSelectedInstitutionFiles(null)
              setSelectedInstitution(null)
            }}
            userContextFiltered
          />
          {institution !== null && (
            <AreaManager
              entityName='Institution'
              entity={selectedInstitutionArea}
              onClose={() => setSelectedInstitutionArea(null)}
              onUpdate={reload}
              userContextFiltered
            />
          )}
        </div>
      </div>
    )
  }

  return render()
}

export default InstitutionParameters
